import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCalendar,
  FiChevronRight,
  FiClock,
  FiFileText,
  FiMail,
  FiPieChart,
  FiRepeat,
  FiSend,
  FiTrendingUp,
  FiUserPlus,
} from "react-icons/fi";
import { FaWallet } from "react-icons/fa";
import MainLayout from "../../layout/MainLayout";
import { accountsApi } from "../../api/endpoints";
import {
  computeTotalBalance,
  useAccounts,
  useBeneficiaries,
  useBills,
  useBudgets,
  useCards,
  useCreditReport,
  useConversions,
  useGoals,
  useGroups,
  useInvoices,
  useInvestments,
  useMoneyRequests,
  useReferralInvites,
  useRetirementPlans,
  useScheduledTransfers,
  useTransactions,
  useTransfers,
} from "../../data/live";

import {
  FLOW_SCREENS,
  SCREEN_BY_SLUG,
  SCREEN_GROUPS,
  familyFor,
  shortTitle,
  siblingScreens,
} from "./flowRegistry";

export { FIGMA_FLOW_ROUTES, FLOW_SCREENS } from "./flowRegistry";

const FlowButton = ({ children, to, variant = "primary", icon: Icon, type = "button" }) => {
  const content = (
    <>
      {Icon && <Icon size={16} />}
      <span>{children}</span>
    </>
  );

  if (to) {
    return (
      <Link to={to} className={`flow-button ${variant}`}>
        {content}
      </Link>
    );
  }

  return (
    <button type={type} className={`flow-button ${variant}`}>
      {content}
    </button>
  );
};

const FlowHeader = ({ screen }) => {
  const index = FLOW_SCREENS.findIndex((item) => item.slug === screen.slug);
  const previous = FLOW_SCREENS[index - 1];
  const next = FLOW_SCREENS[index + 1];

  return (
    <div className="flow-topbar">
      <div>
        <div className="flow-kicker">{screen.group}</div>
        <h2>{shortTitle(screen.title)}</h2>
      </div>
      <div className="flow-topbar-actions">
        <FlowButton to="/figma-screens" variant="ghost" icon={FiArrowLeft}>
          Flows
        </FlowButton>
        {previous && (
          <FlowButton to={`/figma-screens/${previous.slug}`} variant="ghost" icon={FiArrowLeft}>
            Previous
          </FlowButton>
        )}
        {next && (
          <FlowButton to={`/figma-screens/${next.slug}`} variant="ghost" icon={FiArrowRight}>
            Next
          </FlowButton>
        )}
      </div>
    </div>
  );
};

const FlowTabs = ({ screen }) => {
  const siblings = siblingScreens(screen);
  if (siblings.length < 2) return null;

  return (
    <div className="flow-tabs">
      {siblings.map((item) => (
        <Link
          key={item.slug}
          to={`/figma-screens/${item.slug}`}
          className={item.slug === screen.slug ? "active" : ""}
        >
          {item.title.replace(`${item.group} - `, "").replace(item.group, "Overview")}
        </Link>
      ))}
    </div>
  );
};

const Panel = ({ children, title, action, className = "" }) => (
  <section className={`flow-panel ${className}`}>
    {(title || action) && (
      <div className="flow-panel-header">
        {title && <h3>{title}</h3>}
        {action}
      </div>
    )}
    {children}
  </section>
);

const StatCard = ({ label, value, meta, icon: Icon = FiTrendingUp, tone = "" }) => (
  <div className={`flow-stat-card ${tone}`}>
    <div className="flow-stat-icon">
      <Icon size={18} />
    </div>
    <span>{label}</span>
    <strong>{value}</strong>
    {meta && <small>{meta}</small>}
  </div>
);

const Field = ({ label, value, type = "text", options, wide, rows }) => (
  <label className={wide ? "flow-field wide" : "flow-field"}>
    <span>{label}</span>
    {rows ? (
      <textarea defaultValue={value || ""} rows={rows} />
    ) : options ? (
      <select defaultValue={value || options[0]}>
        {options.map((option) => (
          <option key={option}>{option}</option>
        ))}
      </select>
    ) : (
      <input type={type} defaultValue={value || ""} placeholder={label} />
    )}
  </label>
);

const ReviewList = ({ items }) => (
  <dl className="flow-review-list">
    {items.map(([label, value]) => (
      <div key={label}>
        <dt>{label}</dt>
        <dd>{value}</dd>
      </div>
    ))}
  </dl>
);

const formatMoney = (amount, currency = "AUD") => {
  const value = Number(amount || 0);
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: currency || "AUD",
  }).format(Number.isFinite(value) ? value : 0);
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const accountName = (account) =>
  account?.name ||
  account?.accountName ||
  account?.nickname ||
  `${account?.currency || "Account"} wallet`;

const accountBalance = (account) =>
  formatMoney(account?.availableBalance ?? account?.balance, account?.currency);

const beneficiaryName = (beneficiary) =>
  beneficiary?.name ||
  beneficiary?.fullName ||
  beneficiary?.accountName ||
  beneficiary?.businessName ||
  beneficiary?.email ||
  "Beneficiary";

const beneficiaryDetail = (beneficiary) =>
  beneficiary?.bankName ||
  beneficiary?.bank ||
  beneficiary?.payId ||
  beneficiary?.email ||
  beneficiary?.accountNumber ||
  beneficiary?.phone ||
  "";

const LiveEmpty = ({ title = "No backend records found", detail }) => (
  <div className="flow-live-empty">
    <FiFileText size={28} />
    <strong>{title}</strong>
    {detail && <span>{detail}</span>}
  </div>
);

const LiveLoading = () => (
  <div className="flow-live-empty">
    <FiClock size={28} />
    <strong>Loading backend data...</strong>
  </div>
);

const LiveEndpointGap = ({ feature }) => (
  <LiveEmpty
    title={`${feature} backend endpoint is not connected`}
    detail="No fabricated data is shown on this screen."
  />
);

const LiveStatGrid = ({ accounts, transactions, transfers }) => {
  const { total, displayCurrency } = computeTotalBalance(accounts, "AUD");
  const pendingTransfers = transfers.filter((item) =>
    ["PENDING", "SCHEDULED", "PROCESSING"].includes(String(item.status || "").toUpperCase())
  );

  return (
    <div className="flow-stat-grid four">
      <StatCard
        label="Total balance"
        value={formatMoney(total, displayCurrency)}
        meta={`${accounts.length} backend account${accounts.length === 1 ? "" : "s"}`}
        icon={FaWallet}
      />
      <StatCard
        label="Transactions"
        value={String(transactions.length)}
        meta="From /transactions"
        icon={FiFileText}
        tone="green"
      />
      <StatCard
        label="Transfers"
        value={String(transfers.length)}
        meta="From /transfers"
        icon={FiSend}
        tone="blue"
      />
      <StatCard
        label="Pending"
        value={String(pendingTransfers.length)}
        meta="Live transfer status"
        icon={FiClock}
        tone="mint"
      />
    </div>
  );
};

const LiveTable = ({ columns, rows, loading, emptyTitle }) => {
  if (loading) return <LiveLoading />;
  if (!rows.length) return <LiveEmpty title={emptyTitle} />;

  return (
    <div className="flow-table-wrap">
      <table className="flow-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              {columns.map((column) => (
                <td key={column.key}>{row[column.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const LiveAccountsPanel = ({ accounts, loading }) => {
  const rows = accounts.map((account) => ({
    id: account.id || account.accountNumber || account.iban,
    name: accountName(account),
    currency: account.currency || "",
    balance: accountBalance(account),
    status: account.status || account.state || "",
  }));

  return (
    <Panel title="Accounts from backend">
      <LiveTable
        loading={loading}
        emptyTitle="No accounts returned by backend"
        columns={[
          { key: "name", label: "Account" },
          { key: "currency", label: "Currency" },
          { key: "balance", label: "Balance" },
          { key: "status", label: "Status" },
        ]}
        rows={rows}
      />
    </Panel>
  );
};

const LiveTransactionsPanel = ({ transactions, loading }) => {
  const rows = transactions.map((item) => ({
    id: item.id || item.reference,
    date: item.date || formatDate(item.createdAt),
    name: item.counterparty || item.description || item.reference || item.type,
    type: item.type || "",
    amount: formatMoney(item.amount, item.currency),
    status: item.status || "",
  }));

  return (
    <Panel title="Transactions from backend">
      <LiveTable
        loading={loading}
        emptyTitle="No transactions returned by backend"
        columns={[
          { key: "date", label: "Date" },
          { key: "name", label: "Description" },
          { key: "type", label: "Type" },
          { key: "amount", label: "Amount" },
          { key: "status", label: "Status" },
        ]}
        rows={rows}
      />
    </Panel>
  );
};

const LiveBeneficiariesPanel = ({ beneficiaries, loading }) => {
  const rows = beneficiaries.map((beneficiary) => ({
    id: beneficiary.id || beneficiary.accountNumber || beneficiary.email,
    name: beneficiaryName(beneficiary),
    detail: beneficiaryDetail(beneficiary),
    currency: beneficiary.currency || beneficiary.destinationCurrency || "",
    status: beneficiary.status || "",
  }));

  return (
    <Panel title="Recipients from backend" action={<FlowButton to="/add-recipient" icon={FiUserPlus}>Add recipient</FlowButton>}>
      <LiveTable
        loading={loading}
        emptyTitle="No recipients returned by backend"
        columns={[
          { key: "name", label: "Name" },
          { key: "detail", label: "Details" },
          { key: "currency", label: "Currency" },
          { key: "status", label: "Status" },
        ]}
        rows={rows}
      />
    </Panel>
  );
};

const LiveTransfersPanel = ({ transfers, loading }) => {
  const rows = transfers.map((transfer) => ({
    id: transfer.id || transfer.reference,
    date: formatDate(transfer.createdAt || transfer.scheduledFor),
    recipient: transfer.beneficiaryName || transfer.counterparty || transfer.reference,
    amount: formatMoney(transfer.amount, transfer.currency || transfer.sourceCurrency),
    status: transfer.status || "",
  }));

  return (
    <Panel title="Transfers from backend" action={<FlowButton to="/new-transfer" icon={FiSend}>Open transfer flow</FlowButton>}>
      <LiveTable
        loading={loading}
        emptyTitle="No transfers returned by backend"
        columns={[
          { key: "date", label: "Date" },
          { key: "recipient", label: "Recipient" },
          { key: "amount", label: "Amount" },
          { key: "status", label: "Status" },
        ]}
        rows={rows}
      />
    </Panel>
  );
};

const LiveBillsPanel = ({ bills, billers, loading }) => {
  const source = bills.length ? bills : billers;
  const rows = source.map((item) => ({
    id: item.id || item.billerCode || item.name,
    name: item.name || item.billerName || item.description,
    date: formatDate(item.dueDate || item.nextDueDate),
    amount: item.amount ? formatMoney(item.amount, item.currency || "AUD") : "",
    status: item.status || item.frequency || "",
  }));

  return (
    <Panel title={bills.length ? "Bills from backend" : "Billers from backend"} action={<FlowButton to="/my-bills" icon={FiFileText}>Open bills</FlowButton>}>
      <LiveTable
        loading={loading}
        emptyTitle="No bills or billers returned by backend"
        columns={[
          { key: "name", label: "Name" },
          { key: "date", label: "Due date" },
          { key: "amount", label: "Amount" },
          { key: "status", label: "Status" },
        ]}
        rows={rows}
      />
    </Panel>
  );
};

const LiveBudgetsPanel = ({ budgets, loading }) => {
  const rows = budgets.map((budget) => ({
    id: budget.id || budget.category || budget.name,
    name: budget.name || budget.category,
    period: budget.period || budget.month || "",
    amount: formatMoney(budget.amount || budget.limit || budget.budgetAmount, budget.currency || "AUD"),
    spent: budget.spent != null ? formatMoney(budget.spent, budget.currency || "AUD") : "",
  }));

  return (
    <Panel title="Budgets from backend" action={<FlowButton to="/budget-setup" icon={FiPieChart}>Open budget</FlowButton>}>
      <LiveTable
        loading={loading}
        emptyTitle="No budgets returned by backend"
        columns={[
          { key: "name", label: "Budget" },
          { key: "period", label: "Period" },
          { key: "amount", label: "Amount" },
          { key: "spent", label: "Spent" },
        ]}
        rows={rows}
      />
    </Panel>
  );
};

const LiveInvoicesPanel = ({ invoices, loading }) => {
  const rows = invoices.map((invoice) => ({
    id: invoice.id || invoice.invoiceNumber,
    invoice: invoice.invoiceNumber || invoice.id,
    customer: invoice.customerName || invoice.clientName || invoice.email || "",
    amount: formatMoney(invoice.total || invoice.amount, invoice.currency || "AUD"),
    status: invoice.status || "",
  }));

  return (
    <Panel title="Invoices from backend" action={<FlowButton to="/invoice-generator" icon={FiFileText}>Create invoice</FlowButton>}>
      <LiveTable
        loading={loading}
        emptyTitle="No invoices returned by backend"
        columns={[
          { key: "invoice", label: "Invoice" },
          { key: "customer", label: "Customer" },
          { key: "amount", label: "Amount" },
          { key: "status", label: "Status" },
        ]}
        rows={rows}
      />
    </Panel>
  );
};

const LiveRequestsPanel = ({ requests, loading }) => {
  const rows = requests.map((request) => ({
    id: request.id || request.reference,
    recipient: request.recipientName || request.payerName || request.email || "",
    amount: formatMoney(request.amount, request.currency || "AUD"),
    status: request.status || "",
    date: formatDate(request.createdAt || request.dueDate),
  }));

  return (
    <Panel title="Money requests from backend" action={<FlowButton to="/request-money" icon={FiMail}>Open request flow</FlowButton>}>
      <LiveTable
        loading={loading}
        emptyTitle="No money requests returned by backend"
        columns={[
          { key: "recipient", label: "Recipient" },
          { key: "amount", label: "Amount" },
          { key: "status", label: "Status" },
          { key: "date", label: "Date" },
        ]}
        rows={rows}
      />
    </Panel>
  );
};

const LiveCardsPanel = ({ cards, loading }) => {
  const rows = cards.map((card) => ({
    id: card.id,
    holder: card.holderName,
    label: card.label,
    type: card.cardType,
    limit: formatMoney(card.monthlyLimit, card.currency),
    status: card.status,
  }));

  return (
    <Panel title="Cards from backend" action={<FlowButton to="/card" icon={FiFileText}>Open cards</FlowButton>}>
      <LiveTable
        loading={loading}
        emptyTitle="No cards returned by backend"
        columns={[
          { key: "holder", label: "Holder" },
          { key: "label", label: "Card" },
          { key: "type", label: "Type" },
          { key: "limit", label: "Monthly limit" },
          { key: "status", label: "Status" },
        ]}
        rows={rows}
      />
    </Panel>
  );
};

const LiveCreditPanel = ({ profile, loading }) => (
  <Panel title="Credit report from backend">
    {loading ? (
      <LiveLoading />
    ) : profile ? (
      <ReviewList
        items={[
          ["Score", profile.score ?? ""],
          ["Rating", profile.rating || ""],
          ["Bureau", profile.bureau || ""],
          ["Pulled at", formatDate(profile.pulledAt)],
          ["Factors", Array.isArray(profile.factors) ? String(profile.factors.length) : "0"],
        ].filter(([, value]) => value !== "")}
      />
    ) : (
      <LiveEmpty
        title="No credit profile returned by backend"
        detail="The backend will return real cached bureau/import data when available."
      />
    )}
  </Panel>
);

const LiveReferralPanel = ({ summary, invites, loading }) => {
  const rows = invites.map((invite) => ({
    id: invite.id,
    email: invite.inviteeEmail,
    name: invite.inviteeName || "",
    code: invite.referralCode,
    status: invite.status,
  }));

  return (
    <>
      <Panel title="Referral summary from backend">
        {loading ? (
          <LiveLoading />
        ) : summary ? (
          <ReviewList
            items={[
              ["Referral code", summary.referralCode],
              ["Sent", summary.sent],
              ["Accepted", summary.accepted],
              ["Pending", summary.pending],
            ]}
          />
        ) : (
          <LiveEmpty title="No referral summary returned by backend" />
        )}
      </Panel>
      <Panel title="Invites from backend">
        <LiveTable
          loading={loading}
          emptyTitle="No referral invites returned by backend"
          columns={[
            { key: "email", label: "Email" },
            { key: "name", label: "Name" },
            { key: "code", label: "Code" },
            { key: "status", label: "Status" },
          ]}
          rows={rows}
        />
      </Panel>
    </>
  );
};

const LiveGroupsPanel = ({ groups, loading }) => {
  const rows = groups.map((group) => ({
    id: group.id,
    name: group.name,
    members: Array.isArray(group.members) ? String(group.members.length) : "0",
    created: formatDate(group.createdAt),
  }));

  return (
    <Panel title="Split groups from backend" action={<FlowButton to="/split-payment" icon={FiFileText}>Open split payment</FlowButton>}>
      <LiveTable
        loading={loading}
        emptyTitle="No split groups returned by backend"
        columns={[
          { key: "name", label: "Group" },
          { key: "members", label: "Members" },
          { key: "created", label: "Created" },
        ]}
        rows={rows}
      />
    </Panel>
  );
};

const LiveInvestmentsPanel = ({ investments, goals, investmentsLoading, goalsLoading }) => {
  const investmentRows = investments.map((item) => ({
    id: item.id,
    asset: item.assetName,
    type: item.assetType,
    invested: formatMoney(item.amountInvested, item.currency),
    value: formatMoney(item.currentValuation, item.currency),
  }));
  const goalRows = goals.map((goal) => ({
    id: goal.id,
    name: goal.name,
    target: formatMoney(goal.targetAmount, goal.currency),
    current: formatMoney(goal.currentAmount, goal.currency),
    deadline: formatDate(goal.deadline),
  }));

  return (
    <>
      <Panel title="Investments from backend">
        <LiveTable
          loading={investmentsLoading}
          emptyTitle="No investments returned by backend"
          columns={[
            { key: "asset", label: "Asset" },
            { key: "type", label: "Type" },
            { key: "invested", label: "Invested" },
            { key: "value", label: "Current value" },
          ]}
          rows={investmentRows}
        />
      </Panel>
      <Panel title="Goals from backend">
        <LiveTable
          loading={goalsLoading}
          emptyTitle="No goals returned by backend"
          columns={[
            { key: "name", label: "Goal" },
            { key: "target", label: "Target" },
            { key: "current", label: "Current" },
            { key: "deadline", label: "Deadline" },
          ]}
          rows={goalRows}
        />
      </Panel>
    </>
  );
};

const LiveConversionsPanel = ({ conversions, loading }) => {
  const rows = conversions.map((conversion) => ({
    id: conversion.id || conversion.reference,
    from: `${conversion.fromCurrency || conversion.sourceCurrency || ""} ${conversion.sourceAmount || conversion.amount || ""}`.trim(),
    to: `${conversion.toCurrency || conversion.destCurrency || ""} ${conversion.destAmount || ""}`.trim(),
    rate: conversion.rate || conversion.exchangeRate || "",
    status: conversion.status || "",
  }));

  return (
    <Panel title="Conversions from backend" action={<FlowButton to="/convert" icon={FiRepeat}>Open conversion</FlowButton>}>
      <LiveTable
        loading={loading}
        emptyTitle="No conversions returned by backend"
        columns={[
          { key: "from", label: "From" },
          { key: "to", label: "To" },
          { key: "rate", label: "Rate" },
          { key: "status", label: "Status" },
        ]}
        rows={rows}
      />
    </Panel>
  );
};

const LiveScheduledPanel = ({ scheduledTransfers, loading }) => {
  const rows = scheduledTransfers.map((transfer) => ({
    id: transfer.id || transfer.reference,
    recipient: transfer.beneficiaryName || transfer.counterparty || "",
    amount: formatMoney(transfer.amount, transfer.currency || "AUD"),
    schedule: transfer.frequency || transfer.schedule || "",
    nextRun: formatDate(transfer.nextRunAt || transfer.scheduledFor),
  }));

  return (
    <Panel title="Scheduled transfers from backend" action={<FlowButton to="/schedule-transfer" icon={FiCalendar}>Open schedule flow</FlowButton>}>
      <LiveTable
        loading={loading}
        emptyTitle="No scheduled transfers returned by backend"
        columns={[
          { key: "recipient", label: "Recipient" },
          { key: "amount", label: "Amount" },
          { key: "schedule", label: "Schedule" },
          { key: "nextRun", label: "Next run" },
        ]}
        rows={rows}
      />
    </Panel>
  );
};

const LiveQrReceive = ({ accounts }) => {
  const [instructions, setInstructions] = useState(null);
  const [loading, setLoading] = useState(false);
  const account = useMemo(
    () =>
      accounts.find((item) => item.currency === "AUD" && item.isDefault) ||
      accounts.find((item) => item.currency === "AUD") ||
      accounts[0],
    [accounts]
  );

  useEffect(() => {
    if (!account?.id) {
      setInstructions(null);
      return;
    }
    let active = true;
    setLoading(true);
    accountsApi
      .depositInstructions(account.id)
      .then((data) => {
        if (active) setInstructions(data);
      })
      .catch(() => {
        if (active) setInstructions(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [account?.id]);

  if (loading) return <LiveLoading />;
  if (!account) return <LiveEmpty title="No backend account available for QR receive" />;
  if (!instructions) {
    return (
      <LiveEmpty
        title="Deposit instructions were not returned by backend"
        detail="QR receive will appear when /accounts/:id/deposit-instructions responds."
      />
    );
  }

  const qrValue =
    instructions.payid ||
    [
      instructions.accountName,
      instructions.bsb,
      instructions.accountNumber,
      instructions.reference,
    ]
      .filter(Boolean)
      .join("\n");

  return (
    <Panel title="QR receive from backend">
      <div className="flow-qr-box">
        <QRCodeSVG value={qrValue || account.id} size={214} level="M" />
      </div>
      <ReviewList
        items={[
          ["Account", accountName(account)],
          ["PayID", instructions.payid || ""],
          ["BSB", instructions.bsb || ""],
          ["Account number", instructions.accountNumber || ""],
          ["Reference", instructions.reference || ""],
        ].filter(([, value]) => value)}
      />
    </Panel>
  );
};

const LiveFormShell = ({ title, accounts, beneficiaries, billers, target }) => (
  <Panel title={title}>
    <div className="flow-form-grid">
      <Field
        label="Source account"
        options={accounts.map((account) => accountName(account))}
      />
      {target === "beneficiary" && (
        <Field
          label="Recipient"
          options={beneficiaries.map((beneficiary) => beneficiaryName(beneficiary))}
        />
      )}
      {target === "biller" && (
        <Field
          label="Biller"
          options={billers.map((biller) => biller.name || biller.billerName || biller.billerCode)}
        />
      )}
      <Field label="Amount" />
      <Field label="Reference" />
      <Field label="Description" wide rows={3} />
    </div>
    <div className="flow-footer-actions">
      <FlowButton variant="secondary">Backend form only</FlowButton>
    </div>
  </Panel>
);

const LiveBackendFlow = ({ screen }) => {
  const family = familyFor(screen.slug);
  const { accounts, loading: accountsLoading } = useAccounts();
  const { beneficiaries, loading: beneficiariesLoading } = useBeneficiaries();
  const { items: transactions, loading: transactionsLoading } = useTransactions({ limit: 50 });
  const { transfers, loading: transfersLoading } = useTransfers();
  const { conversions, loading: conversionsLoading } = useConversions();
  const { items: scheduledTransfers, loading: scheduledLoading } = useScheduledTransfers();
  const { items: moneyRequests, loading: requestsLoading } = useMoneyRequests();
  const { items: invoices, loading: invoicesLoading } = useInvoices();
  const { bills, billers, loading: billsLoading } = useBills();
  const { items: budgets, loading: budgetsLoading } = useBudgets();
  const { items: cards, loading: cardsLoading } = useCards();
  const { profile: creditProfile, loading: creditLoading } = useCreditReport();
  const {
    summary: referralSummary,
    invites: referralInvites,
    loading: referralLoading,
  } = useReferralInvites();
  const { items: groups, loading: groupsLoading } = useGroups();
  const { items: investments, loading: investmentsLoading } = useInvestments();
  const { items: goals, loading: goalsLoading } = useGoals();
  const {
    plans: retirementPlans,
    projection,
    loading: retirementLoading,
  } = useRetirementPlans();

  const showOverview =
    family === "dashboard" ||
    family === "wallet" ||
    family === "accounts" ||
    family === "overview" ||
    family === "statement";

  return (
    <div className="flow-content">
      <FlowTabs screen={screen} />
      {showOverview && (
        <LiveStatGrid
          accounts={accounts}
          transactions={transactions}
          transfers={transfers}
        />
      )}

      {family === "dashboard" && (
        <>
          <LiveAccountsPanel accounts={accounts} loading={accountsLoading} />
          <LiveTransactionsPanel transactions={transactions} loading={transactionsLoading} />
        </>
      )}

      {family === "wallet" && (
        <>
          <LiveAccountsPanel accounts={accounts} loading={accountsLoading} />
          <LiveTransactionsPanel transactions={transactions} loading={transactionsLoading} />
        </>
      )}

      {family === "accounts" && (
        <LiveAccountsPanel accounts={accounts} loading={accountsLoading} />
      )}

      {family === "transactions" && (
        <LiveTransactionsPanel transactions={transactions} loading={transactionsLoading} />
      )}

      {family === "recipients" && (
        <LiveBeneficiariesPanel beneficiaries={beneficiaries} loading={beneficiariesLoading} />
      )}

      {family === "sendMoney" && (
        <>
          <LiveFormShell
            title="Transfer form wired to backend resources"
            accounts={accounts}
            beneficiaries={beneficiaries}
            billers={billers}
            target={screen.slug.includes("bpay") ? "biller" : "beneficiary"}
          />
          <LiveTransfersPanel transfers={transfers} loading={transfersLoading} />
        </>
      )}

      {family === "addFunds" && (
        <>
          <LiveFormShell
            title="Add funds form wired to backend accounts"
            accounts={accounts}
            beneficiaries={[]}
            billers={[]}
          />
          <LiveAccountsPanel accounts={accounts} loading={accountsLoading} />
        </>
      )}

      {family === "convert" && (
        <LiveConversionsPanel conversions={conversions} loading={conversionsLoading} />
      )}

      {family === "schedule" && (
        <LiveScheduledPanel scheduledTransfers={scheduledTransfers} loading={scheduledLoading} />
      )}

      {family === "request" && (
        <LiveRequestsPanel requests={moneyRequests} loading={requestsLoading} />
      )}

      {family === "invoice" && (
        <LiveInvoicesPanel invoices={invoices} loading={invoicesLoading} />
      )}

      {(family === "spending" || family === "bills") && (
        <>
          <LiveBudgetsPanel budgets={budgets} loading={budgetsLoading} />
          <LiveBillsPanel bills={bills} billers={billers} loading={billsLoading} />
        </>
      )}

      {family === "retirement" && (
        <Panel title="Retirement plans from backend">
          {retirementLoading ? (
            <LiveLoading />
          ) : retirementPlans.length || projection ? (
            <ReviewList
              items={[
                ["Plans", String(retirementPlans.length)],
                ["Projection", projection?.projectedBalance ? formatMoney(projection.projectedBalance, "AUD") : ""],
                ["Target age", projection?.retirementAge || ""],
              ].filter(([, value]) => value)}
            />
          ) : (
            <LiveEmpty title="No retirement plan returned by backend" />
          )}
        </Panel>
      )}

      {family === "cards" && (
        <LiveCardsPanel cards={cards} loading={cardsLoading} />
      )}

      {family === "credit" && (
        <LiveCreditPanel profile={creditProfile} loading={creditLoading} />
      )}

      {family === "invite" && (
        <LiveReferralPanel
          summary={referralSummary}
          invites={referralInvites}
          loading={referralLoading}
        />
      )}

      {family === "split" && (
        <LiveGroupsPanel groups={groups} loading={groupsLoading} />
      )}

      {family === "invest" && (
        <LiveInvestmentsPanel
          investments={investments}
          goals={goals}
          investmentsLoading={investmentsLoading}
          goalsLoading={goalsLoading}
        />
      )}

      {family === "qr" && (
        screen.slug.includes("receive") ? (
          <LiveQrReceive accounts={accounts} />
        ) : (
          <LiveEndpointGap feature="QR pay" />
        )
      )}

      {family === "statement" && (
        <LiveTransactionsPanel transactions={transactions} loading={transactionsLoading} />
      )}

      {family === "overview" && (
        <>
          <LiveAccountsPanel accounts={accounts} loading={accountsLoading} />
          <LiveBillsPanel bills={bills} billers={billers} loading={billsLoading} />
        </>
      )}

    </div>
  );
};

const renderScreen = (screen) => <LiveBackendFlow screen={screen} />;

export const FigmaScreensIndex = () => (
  <MainLayout NavName="ARM Flows" expand={false} hideKyc pageClassName="figma-flow-page">
    <div className="figma-screens-index flow-index-page">
      <div className="page-action-header">
        <h2>ARM Financial Flows</h2>
        <FlowButton to="/dashboard" variant="ghost" icon={FiArrowLeft}>
          Dashboard
        </FlowButton>
      </div>
      {SCREEN_GROUPS.map((group) => (
        <section className="figma-screen-group" key={group.group}>
          <h3>{group.group}</h3>
          <div className="figma-screen-grid flow-index-grid">
            {group.screens.map((screen) => (
              <Link
                to={`/figma-screens/${screen.slug}`}
                className="figma-screen-card flow-index-card"
                key={screen.slug}
              >
                <span>{screen.title}</span>
                <small>{screen.aliases[0] || `/flows/${screen.slug}`}</small>
                <FiChevronRight />
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  </MainLayout>
);

export const FigmaScreenRoute = ({ screenSlug }) => {
  const params = useParams();
  const slug = screenSlug || params.slug;
  const screen = SCREEN_BY_SLUG[slug];

  if (!screen) {
    return (
      <MainLayout NavName="ARM Flows" expand={false} hideKyc pageClassName="figma-flow-page">
        <div className="figma-screen-state error">Flow screen not found.</div>
      </MainLayout>
    );
  }

  return (
    <MainLayout NavName={screen.group} expand={false} hideKyc pageClassName="figma-flow-page">
      <div className="flow-page-shell">
        <FlowHeader screen={screen} />
        {renderScreen(screen)}
      </div>
    </MainLayout>
  );
};
